import { Component } from '@theme/component';
import { MediaGallery } from '@theme/media-gallery';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { getCheckedPurchaseOption, restorePurchaseOption } from '@theme/purchase-options';
import { buildSectionSelector, sectionRenderer } from '@theme/section-renderer';
import { buildSearchUrl, findVariantBySize } from '@theme/ss-utils';
import { decodeEntities, productHandleFromUrl } from '@theme/utilities';

/**
 * Keep sold-out prints out of the selector. `.low=1` is Searchspring's range syntax for "at least one", and
 * a background filter applies before facets are counted.
 */
const IN_STOCK_BGFILTER = {
  field: 'bgfilter.mfield_denver_inventory_total_product_quantity.low',
  value: '1',
};

/**
 * The most Searchspring returns per response, whatever is asked for. A product type can exceed it, a
 * tag group cannot, which is why groups are queried rather than paged.
 */
const PAGE_SIZE = 100;

/** Pairs nodes across a morph by the `data-key` the theme marks them with. */
const nodeKey = (/** @type {Node} */ node) => (node instanceof HTMLElement ? node.dataset.key : undefined);

/** Matches a rendered swatch link, from the swatch itself or its inner image. */
const SWATCH_SELECTOR = 'a.product-swatches__swatch';

/** The rendered swatch diameter, kept in step with the CSS so the `img` carries real dimensions. */
const SWATCH_SIZE = 38;

/** `srcset` densities. `x` descriptors, not `w`: the circle is a fixed 38px, so `sizes` adds nothing. */
const SWATCH_IMAGE_DPRS = [1, 2];

/**
 * How far a product shot is scaled to crop its baked-in whitespace. Kept in step with
 * `.product-swatches__swatch-image--product-shot`, and its image is requested this much larger.
 */
const PRODUCT_SHOT_SCALE = 4;

/**
 * The resize parameter each CDN understands. Neither honours the other's, and an unlisted host is left
 * alone.
 *
 * @type {Record<string, (width: number) => [string, string]>}
 */
const RESIZE_PARAM = {
  'cdn.shinesty.com': (width) => ['size', `${width}w`],
  'cdn.shopify.com': (width) => ['width', String(width)],
};

/**
 * Get an image url at the requested width. Built through `URL` so the parameter joins onto the Shopify
 * CDN's existing `?v=` instead of adding a second `?`.
 *
 * @param {string} src
 * @param {number} width
 * @returns {string}
 */
const sizedImageUrl = (src, width) => {
  try {
    const url = new URL(src, window.location.origin);
    const param = RESIZE_PARAM[url.hostname];
    if (!param) return src;

    url.searchParams.set(...param(width));

    return url.toString();
  } catch {
    return src;
  }
};

/**
 * Where a result names its print swatch, most preferred first. `custom.swatch_img` indexes as
 * `mfield_custom_swatch_img`, the same shape as `mfield_custom_feature`. `button_image` is the legacy
 * cdn.shinesty.com url and stays until every site id has reindexed on the metafield.
 */
const SWATCH_IMAGE_FIELDS = ['mfield_custom_swatch_img', 'button_image'];

/**
 * Get a result's print swatch url. A metafield arrives as a single-entry array and entity-escaped —
 * `mfield_custom_feature` is `['With Fly']` — where `button_image` is a bare string.
 *
 * @param {any} result
 * @returns {string} Empty where the result names no swatch.
 */
const swatchImageUrl = (result) => {
  for (const field of SWATCH_IMAGE_FIELDS) {
    const value = result?.[field];
    const first = Array.isArray(value) ? value[0] : value;
    if (typeof first === 'string' && first) return decodeEntities(first);
  }

  return '';
};

/**
 * Get a result's print name: the first `|`-delimited segment of its title, as the block splits
 * `product.title`.
 *
 * @param {any} result
 * @returns {string}
 */
const getProductTitle = (result) => (result?.name ? decodeEntities(result.name).split('|')[0].trim() : '');

/**
 * Whether a result carries a tag, whichever shape Searchspring returned its tags in.
 *
 * @param {any} result
 * @param {string} tag
 * @returns {boolean}
 */
const hasTag = (result, tag) => {
  const tags = result?.tags ?? result?.ss_tags ?? [];

  return (Array.isArray(tags) ? tags : [tags]).some((entry) => String(entry) === tag);
};

/**
 * Renders a row of cross-product print swatches from Searchspring. Every print is a separate product,
 * so the selector is a search result rather than a variant list, and the query is built entirely from
 * attributes — this component knows nothing about product types, tags or groups.
 *
 * Filter field names live in the attribute VALUE so one can repeat (`tags=a&tags=b`) and keep its
 * casing, which duplicate attributes and `dataset` could not.
 *
 * - `data-site-id` — Searchspring site id. Required.
 * - `data-product-type` — `filter.product_type`. Its own attribute: every selector is scoped to one.
 * - `data-product-id` — the print to mark as current. Names nothing, and the first swatch is marked.
 * - `data-size` — `filter.ss_size`, the sizes a print has in stock. Changing it refetches.
 * - `data-filters` / `data-bgfilters` — any further `filter.*` / `bgfilter.*` values.
 * - `data-results-per-page` — defaults to 100, which is also the ceiling.
 * - `data-morph-section-id` — the section to re-render on a pick. Omitted, a pick only emits.
 * - `data-update-url` — set only where the selector's product IS the page's, so a swap belongs in history.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} grid
 * @property {HTMLElement} [tablist] - Group tabs, when the host rendered any
 * @property {HTMLElement} [tabIndicator] - The pill that slides onto the open tab
 * @property {HTMLElement} [printName] - Shows the selected print's name, and the hovered one's
 *
 * @extends {Component<Refs>}
 */
export class SsPrintSwatches extends Component {
  /**
   * @static @constant {string} Fired when a swatch is picked. `detail` is the print's Searchspring
   * result. Cancel it to stop the selector re-rendering a section.
   */
  static swatchUpdate = 'ss-print-swatches:update';

  /**
   * @static @constant {string} Fired once on first render, for the print the selector marks —
   * the one `data-product-id` named, or the first swatch when it named none. `detail` is that
   * print's result.
   */
  static swatchInit = 'ss-print-swatches:init';

  requiredRefs = ['grid'];

  static get observedAttributes() {
    return ['data-size', 'data-product-type', 'data-product-id'];
  }

  /**
   * The formatted response for the current query, null before it loads or after a failure. Public so a
   * host can read what the selector is showing.
   *
   * @type {any}
   */
  searchspringData = null;

  /**
   * Holds the first query until the selector is on screen, since one below the fold costs a Searchspring
   * request and up to 100 images. `rootMargin` starts it just before it is reached.
   */
  #inView = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.pending = this.fetchData();
    },
    { rootMargin: '200px' },
  );

  /**
   * Keeps the tab pill on its tab as the row reflows — a resize or a font swap moves the tabs. Observing
   * each tab rather than the row also positions the pill on the first callback, once the tabs have a size.
   */
  #tabResize = new ResizeObserver(() => this.#moveTabIndicator());

  /**
   * The print each product type last showed, so a host that swaps type — the drawer changing cut —
   * comes back to it rather than to the first swatch.
   *
   * @type {Map<string, string>}
   */
  #pickedByType = new Map();

  /** Whether the current query has emitted which print it is marking. */
  #emittedInit = false;

  /** The selected print's name, which survives a group it has no swatch in. */
  #activePrintName = '';

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** The query the rendered selector came from, so a picker change only refetches when it moved. */
  #loadedQuery = /** @type {string | null} */ (null);

  /** @type {boolean} Whether this render is showing another group of an answer already given. */
  #filtering = false;

  /** Whether a pick that no longer fits should be replaced rather than left unanswered. */
  #refit = false;

  /**
   * Results by product id, which is Searchspring's `parentId`. Keyed on the id rather than the element,
   * because element identity does not survive a morph.
   *
   * @type {Map<string, any>}
   */
  #resultsByProductId = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.#activePrintName = this.refs.printName?.textContent.trim() ?? '';
    this.#inView.observe(this);
    for (const tab of this.#tabs) this.#tabResize.observe(tab);
    window.addEventListener('popstate', this.#restoreFromHistory);
    // component.js delegates no touch events, and a tap is deliberate: prefetch without the hover gate
    this.addEventListener('touchstart', this.#prefetchFromTouch, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Named, so a wrapper around `fetch` that logs rejections says which one this was rather
    // than "aborted without reason".
    this.#activeFetch?.abort(new DOMException('Print selector removed', 'AbortError'));
    this.#inView.disconnect();
    this.#tabResize.disconnect();
    window.removeEventListener('popstate', this.#restoreFromHistory);
    this.removeEventListener('touchstart', this.#prefetchFromTouch);
  }

  /**
   * @param {string} name
   * @param {string} _oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, _oldValue, newValue) {
    // a host naming a print is a pick, so it outranks whatever this type last showed
    if (name === 'data-product-id' && newValue) this.#pickedByType.set(this.dataset.productType ?? '', newValue);

    if (!this.#loadedQuery) return;

    if (this.#queryKey !== this.#loadedQuery) {
      // Filtering: the size, the type or the named print moved, which narrows an answer the
      // shopper already gave. Asked afresh, this settles on the first swatch of the open
      // group and emits it, moving the print out from under them.
      //
      // A size is the exception. It does not narrow the question, it rules prints out — one
      // the size has none of is gone from every group, so leaving the pick alone leaves the
      // step with nothing selected at all.
      this.pending = this.fetchData({ filtering: true, refit: name === 'data-size' });
      return;
    }

    // same query, so only the marking can have moved
    const links = /** @type {HTMLAnchorElement[]} */ ([...this.refs.grid.querySelectorAll(SWATCH_SELECTOR)]);
    this.#markSelectedSwatch(links, this.#pickedProductId);
  }

  /**
   * What the selector is currently asking about — every selection and the open group.
   *
   * Not a url: there is one query per selection, so what identifies the question is the whole
   * list rather than any one address.
   *
   * @returns {string}
   */
  get #queryKey() {
    return `${this.#activeTab?.dataset.matchTag ?? ''}:${JSON.stringify(this.#selections)}`;
  }

  /**
   * Build the Searchspring url for one group.
   *
   * @param {string} [groupTag] - Scopes the query to a tag group server-side, so the whole group comes back
   *   rather than the first page of the product type.
   * @param {{ productType: string, size: string }} [selection] - Asks for this pair instead of the
   *   product the selector was rendered for, which is how a matching set asks for each of its own.
   * @returns {string | null} Null when no site id is configured.
   */
  #buildEndpoint(groupTag, selection) {
    const siteId = this.dataset.siteId;
    if (!siteId) return null;

    const params = new URLSearchParams({
      siteId,
      resultsFormat: 'native',
      resultsPerPage: String(this.#resultsPerPage),
    });

    params.set(IN_STOCK_BGFILTER.field, IN_STOCK_BGFILTER.value);

    const productType = selection?.productType ?? this.dataset.productType;
    if (productType) params.set('filter.product_type', productType);

    if (groupTag) params.set('filter.tags', groupTag);

    const size = selection?.size ?? this.dataset.size;
    if (size) params.set('filter.ss_size', size);

    for (const [field, value] of new URLSearchParams(this.dataset.filters ?? '')) {
      params.append(`filter.${field}`, value);
    }

    for (const [field, value] of new URLSearchParams(this.dataset.bgfilters ?? '')) {
      params.append(`bgfilter.${field}`, value);
    }

    return buildSearchUrl(siteId, params);
  }

  /**
   * How many prints to ask for, clamped to the page Searchspring will actually return.
   *
   * @returns {number}
   */
  get #resultsPerPage() {
    const requested = Number(this.dataset.resultsPerPage);

    return Number.isInteger(requested) && requested > 0 ? Math.min(requested, PAGE_SIZE) : PAGE_SIZE;
  }

  /** The query in flight, so a host can wait for the selector to settle. */
  pending = Promise.resolve();

  /**
   * What the selector is answering for, as a list.
   *
   * A product page asks about one garment and a matching set about several, which is a
   * difference of length rather than of kind — so both are a list and the querying below
   * never has to know which it is holding.
   *
   * @returns {{ productType: string, size: string }[]}
   */
  get #selections() {
    try {
      const parsed = JSON.parse(this.dataset.matching ?? '[]');
      const named = Array.isArray(parsed) ? parsed.filter((entry) => entry?.productType && entry?.size) : [];
      if (named.length) return named;
    } catch {
      // Falls through to the one the markup describes.
    }

    const productType = this.dataset.productType;

    return productType ? [{ productType, size: this.dataset.size ?? '' }] : [];
  }

  /**
   * What makes two products the same print.
   *
   * The second segment of the sku is the print's own id, and it is the same on every garment
   * wearing it. A placeholder id says nothing, so those fall back to the name a shopper reads
   * — the part before the pipe.
   *
   * @param {any} result
   * @returns {string}
   */
  #printKey(result) {
    const segment = String(result?.sku ?? '').split('-')[1] ?? '';
    const named = String(result?.name ?? '').split('|')[0].trim();

    return /[1-9]/.test(segment) ? segment : named;
  }

  /**
   * Ask for every selection's prints at once and keep the ones they can all wear.
   *
   * A print is only offered where it exists for each selection, in that selection's own size —
   * so each is asked separately and the answers are intersected. Asking about one garment
   * intersects nothing and returns what it found. The result kept is the first selection's,
   * since that is the garment on screen; every selection's is carried on it as `matches`.
   *
   * @param {{ productType: string, size: string }[]} selections
   * @param {string} [groupTag]
   * @param {AbortSignal} [signal]
   * @returns {Promise<any[]>}
   */
  async #fetchMatching(selections, groupTag, signal) {
    // A group is asked for server-side only where one garment answers for the print. Across
    // several, the same design is tagged differently from one garment to the next — a flamingo
    // that is Patterned as a thong and Apex as a boxer — so asking each for the same group
    // leaves nothing they both belong to. They are asked for everything and grouped below.
    const tagged = selections.length > 1 ? undefined : groupTag;

    const responses = await Promise.all(
      selections.map(async (selection) => {
        const url = this.#buildEndpoint(tagged, selection);
        if (!url) return [];

        const response = await fetch(url, { signal });
        if (!response.ok) throw new Error(`Searchspring request failed: ${response.status}`);

        return this.#formatSearchspringData(await response.json()).results;
      })
    );

    const [first, ...rest] = responses.map(
      (results) => new Map(results.map((result) => [this.#printKey(result), result]))
    );
    if (!first) return [];

    const kept = [];

    for (const [key, result] of first) {
      const matches = rest.map((group) => group.get(key));
      if (matches.some((match) => !match)) continue;

      const print = { ...result, matches: [result, ...matches] };

      // The group any of its garments claims: a print belongs to one group however unevenly
      // its garments are tagged.
      if (tagged === undefined && groupTag && !print.matches.some((match) => hasTag(match, groupTag))) {
        continue;
      }

      kept.push(print);
    }

    return kept;
  }

  /**
   * Fetch the open group's prints and render them, leaving the selector empty if the request fails. Public
   * so a host can refresh the selector — the drawer re-queries each time its panel opens — and the render
   * emits again, since a host that was away needs telling which print is marked.
   */
  async fetchData({ filtering = false, refit = false } = {}) {
    // whoever asked, the selector is being queried now, so the gate has done its job
    this.#inView.disconnect();
    this.#filtering = filtering;
    this.#refit = refit;
    this.#emittedInit = false;

    const selections = this.#selections;
    if (!selections.length) return;

    const groupTag = this.#activeTab?.dataset.matchTag;

    // One query per selection, and what it is cached under names them all — a size or a style
    // changing has to ask again.
    const query = this.#queryKey;

    // Same query as the one already loaded: re-render from what came back rather than
    // asking again. A host calls this to refresh and be told again, which the render below
    // does on its own.
    if (query === this.#loadedQuery && this.searchspringData) {
      this.#render(this.#filterByAllowedHandles(this.#applyTabGroup(this.searchspringData.results)));
      return;
    }

    this.#activeFetch?.abort(new DOMException('Superseded by a newer query', 'AbortError'));
    const controller = new AbortController();
    this.#activeFetch = controller;

    this.refs.grid.setAttribute('aria-busy', 'true');

    try {
      // A payload per selection went into this, so there is no single response to carry.
      const results = await this.#fetchMatching(selections, groupTag, controller.signal);

      if (!controller.signal.aborted) {
        // Indexed from what is about to be shown, not from any one response — a swatch is
        // looked up by the id it was rendered with.
        this.#resultsByProductId = new Map(results.map((result) => [String(result.parentId), result]));
        this.searchspringData = { results };
        this.#loadedQuery = query;
        this.#render(this.#filterByAllowedHandles(this.#applyTabGroup(this.searchspringData.results)));
      }
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error(error);
        this.searchspringData = null;
        this.refs.grid.replaceChildren();
      }
    } finally {
      if (this.#activeFetch === controller) {
        this.refs.grid.removeAttribute('aria-busy');
        this.#activeFetch = null;
      }
    }

  }

  /**
   * Rewrite every result's url to a path on this host, and index the results by product id. The feed
   * holds production urls, so an untouched one would leave a dev store. An unusable url becomes an
   * empty string, which drops the result at render.
   *
   * @param {any} data
   * @returns {any}
   */
  #formatSearchspringData(data) {
    const results = (data.results ?? []).map((/** @type {any} */ result) => {
      let path = '';

      try {
        // guarded: new URL(undefined) resolves to /undefined
        if (typeof result.url === 'string') {
          const url = new URL(result.url, window.location.origin);
          if (url.protocol === 'https:' || url.protocol === 'http:') path = `${url.pathname}${url.search}`;
        }
      } catch {
        // an empty path skips the result
      }

      return { ...result, url: path };
    });

    return { ...data, results };
  }

  /**
   * Render one swatch per result. `on:*` are attributes because component.js delegates from the
   * document, so they bind on nodes created here.
   *
   * @param {any[]} results
   */
  #render(results) {
    const fragment = document.createDocumentFragment();
    /** @type {HTMLAnchorElement[]} */
    const links = [];

    for (const result of results) {
      const printImage = swatchImageUrl(result);
      const productShot = result.image;
      const path = result.url;
      if ((!printImage && !productShot) || !path) continue;

      const link = document.createElement('a');
      link.href = path;
      link.dataset.productId = String(result.parentId);
      link.className = 'product-swatches__swatch';
      link.setAttribute('on:click', '/selectSwatch');
      link.setAttribute('on:pointerenter', '/previewPrint');

      const img = document.createElement('img');
      img.alt = decodeEntities(String(result.name ?? ''));
      img.loading = 'eager';
      img.width = SWATCH_SIZE;
      img.height = SWATCH_SIZE;
      this.#setSwatchImage(img, printImage || productShot, !printImage);

      // error doesn't bubble, so on:error would never fire
      if (printImage && productShot) {
        img.addEventListener('error', () => this.#setSwatchImage(img, productShot, true), { once: true });
      }

      const item = document.createElement('li');
      link.append(img);
      item.append(link);
      fragment.append(item);
      links.push(link);
    }

    this.refs.grid.replaceChildren(fragment);

    // Answering settles on a print — the one named, or the first where that print is not among
    // these — and says so. Filtering only changes which of the answers are on screen, so the
    // shopper's pick stands even while the group they are looking through does not hold it.
    const wanted = this.#pickedProductId;
    const gone = Boolean(wanted) && !links.some((link) => link.dataset.productId === wanted);

    this.#markSelectedSwatch(links, wanted, !this.#filtering || this.#refit);

    if (!this.#filtering && !this.#emittedInit && links.length) {
      this.#emittedInit = true;
      this.emitInitialSelection();
      return;
    }

    // The size ruled the pick out and another print stands in its place, which the host is
    // still showing the old one of.
    if (this.#refit && gone && links.length) this.#announceRefit();
  }

  /**
   * Hand the replacement over once the host has finished the render the size change put in
   * flight. Announced into the middle of that one, the host renders this print and then the
   * older answer lands on top of it.
   */
  #announceRefit() {
    let done = false;
    const emit = () => {
      if (done) return;
      done = true;
      document.removeEventListener('product-builder:print-rendered', emit);
      clearTimeout(timer);
      this.emitInitialSelection();
    };

    // Nothing in flight announces nothing, so this is the whole wait.
    const timer = setTimeout(emit, 1500);
    document.addEventListener('product-builder:print-rendered', emit);
  }

  /** The print to mark: the one picked in this product type before, else the one the host named. */
  get #pickedProductId() {
    return this.#pickedByType.get(this.dataset.productType ?? '') ?? this.dataset.productId;
  }

  /** @returns {HTMLElement[]} */
  get #tabs() {
    return /** @type {HTMLElement[]} */ ([...(this.refs.tablist?.querySelectorAll('[data-group]') ?? [])]);
  }

  /** @returns {HTMLElement | null} The open tab, or null when the product fell into no group. */
  get #activeTab() {
    return this.refs.tablist?.querySelector('[aria-selected="true"]') ?? null;
  }

  /**
   * Open a named group without fetching, for a host restoring the tab a shopper had open
   * before the product under this selector was swapped. The caller's own fetch reads the
   * open tab, so doing it here would ask the same question twice.
   *
   * @param {string | null | undefined} group
   * @returns {boolean} Whether a tab by that name is on offer.
   */
  presetGroup(group) {
    if (!group) return false;

    const tab = this.#tabs.find((candidate) => candidate.dataset.group === group);
    if (!tab) return false;

    for (const other of this.#tabs) other.setAttribute('aria-selected', String(other === tab));
    this.#moveTabIndicator();

    return true;
  }

  /** @returns {string | null} The group currently open, for a host about to swap the product. */
  get openGroup() {
    return this.#activeTab?.dataset.group ?? null;
  }

  /** @returns {string | null} That group's Shopify tag, for a host ranking its own results. */
  get openGroupTag() {
    return this.#activeTab?.dataset.matchTag ?? null;
  }

  /**
   * Emit the print the selector is currently marking — the one `data-product-id` named, or
   * the first swatch where it named none.
   *
   * For a host that needs telling again: a panel reopening on a selector that rendered once
   * already emitted this, and its listener may not have been there to hear it.
   */
  emitInitialSelection() {
    const marked = this.refs.grid?.querySelector(`${SWATCH_SELECTOR}[aria-current]`);
    if (!(marked instanceof HTMLElement)) return;

    const result = this.#resultsByProductId.get(marked.dataset.productId ?? '');

    this.#emit(SsPrintSwatches.swatchInit, result);
  }

  /**
   * Emit a print, with the garments it is made of where it is made of several.
   *
   * A listener is told the same thing whether the shopper picked the print or the selector
   * opened on it, so both go through here rather than one being the poorer cousin.
   *
   * @param {string} name - The event to fire.
   * @param {any} result - The print's Searchspring result.
   * @returns {boolean} Whether it went uncancelled. Always true for a print still being
   *   fetched, which is announced once it arrives.
   */
  #emit(name, result) {
    if (!result) return true;

    if (result.matches?.length > 1) {
      this.#fetchProducts(result).then((products) => {
        this.dispatchEvent(
          new CustomEvent(name, {
            bubbles: true,
            cancelable: true,
            detail: { ...result, products: products.filter(Boolean) },
          })
        );
      });

      return true;
    }

    return this.dispatchEvent(new CustomEvent(name, { bubbles: true, cancelable: true, detail: result }));
  }

  /**
   * Open one group and re-query for it.
   *
   * @param {Event} event
   */
  activateTab(event) {
    const tab = event.target;
    if (!(tab instanceof HTMLElement)) return;

    for (const other of this.#tabs) other.setAttribute('aria-selected', String(other === tab));

    this.#moveTabIndicator();
    // The same question, looked at through another group.
    this.pending = this.fetchData({ filtering: true });
  }

  /**
   * Slide the pill onto the open tab, measured live because the labels size the tabs. It jumps rather
   * than slides when it has no position to slide from, and hides when the product fell into no group.
   */
  #moveTabIndicator() {
    const indicator = this.refs.tabIndicator;
    if (!indicator) return;

    const tab = this.#activeTab;

    if (!tab) {
      indicator.dataset.active = 'false';
      return;
    }

    const jump = indicator.dataset.active !== 'true';
    if (jump) indicator.setAttribute('data-instant', '');

    indicator.style.setProperty('--tab-indicator-x', `${tab.offsetLeft}px`);
    indicator.style.setProperty('--tab-indicator-width', `${tab.offsetWidth}px`);
    indicator.dataset.active = 'true';

    if (jump) {
      // Flush the position while transitions are still off — a rAF callback would run before style
      // recalculation, so the pill would slide.
      void indicator.offsetWidth;
      indicator.removeAttribute('data-instant');
    }
  }

  /**
   * Filter the results to the open tab's handle token, or return them all. Tag groups are filtered by
   * the query instead, and `data-match-handle-negate` inverts the test for a catch-all group.
   *
   * @param {any[]} results
   * @returns {any[]}
   */
  #applyTabGroup(results) {
    const { matchHandle, matchHandleNegate } = this.#activeTab?.dataset ?? {};
    if (!matchHandle) return results;

    const negate = matchHandleNegate === 'true';

    return results.filter((result) => result.url.includes(matchHandle) !== negate);
  }

  /**
   * Inside the pack builder, keep only prints that exist as a pair: the builder publishes the
   * assortment products' handles on its `data-allowed-swatch-handles`, and any Searchspring result
   * whose product isn't among them has no pair to switch to, so it's dropped. No-op on a real PDP,
   * where there is no such ancestor, and in the single flow, which never sets the attribute.
   *
   * @param {any[]} results
   * @returns {any[]}
   */
  #filterByAllowedHandles(results) {
    const raw = this.closest('product-builder')?.dataset.allowedSwatchHandles;
    if (!raw) return results;

    let allowed;
    try {
      allowed = new Set(JSON.parse(raw));
    } catch {
      return results;
    }
    if (allowed.size === 0) return results;

    return results.filter((result) => {
      const handle = productHandleFromUrl(result.url);
      return handle ? allowed.has(handle) : false;
    });
  }

  /**
   * Move the selected ring and `aria-current` onto one swatch. Links are passed in because refs are
   * refreshed in a microtask, so straight after a render they still hold the previous set.
   *
   * Three things can name the print, consulted in the order of who knows best: a pick already
   * made in this product type, then `data-product-handle` — a list, since one print can be
   * several garments and any of them addresses its swatch — then `data-product-id`, which is
   * only ever the product this selector was rendered beside. A print missing from this group
   * means the pick lives in another one.
   *
   * @param {HTMLAnchorElement[]} links
   * @param {string} [productId] - The print to mark. Marks the first swatch when no print is named,
   *   and none when the named print is not in this group.
   * @param {boolean} [fallBackToFirst] - Settle on the first swatch where the named print is
   *   not among these. Only a list that answers a question does; one that filters an answer
   *   already given leaves the pick alone.
   */
  #markSelectedSwatch(links, productId, fallBackToFirst = false) {
    const picked = this.#pickedByType.get(this.dataset.productType ?? '');
    const handles = (this.dataset.productHandle ?? '').split(',').filter(Boolean);

    const findById = (id) => (id ? (links.find((link) => link.dataset.productId === id) ?? null) : null);
    const byHandle = handles.length
      ? (links.find((link) => handles.includes(productHandleFromUrl(link.getAttribute('href') ?? ''))) ?? null)
      : null;

    const named = findById(picked) ?? byHandle ?? findById(productId);
    const wanted = picked || handles.length > 0 || productId;
    const current = named ?? (wanted && !fallBackToFirst ? null : (links[0] ?? null));

    if (current) this.#pickedByType.set(this.dataset.productType ?? '', current.dataset.productId ?? '');

    for (const link of links) {
      const isCurrent = link === current;
      link.classList.toggle('product-swatches__swatch--selected', isCurrent);

      if (isCurrent) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    }

    // the selected print has no result in a group it does not belong to, so its name is remembered
    const name = getProductTitle(this.#resultsByProductId.get(current?.dataset.productId ?? ''));
    if (name) this.#activePrintName = name;

    this.#writePrintName(this.#activePrintName);
  }

  /**
   * Write a name into the header, when the host rendered the ref.
   *
   * @param {string} name
   */
  #writePrintName(name) {
    if (this.refs.printName) this.refs.printName.textContent = name;
  }

  /**
   * Point an `img` at a swatch image, sized for the circle. A product shot also gets the scaling class
   * and that much more detail. Both `src` and `srcset` are set, since a stale `srcset` outranks a new
   * `src`.
   *
   * @param {HTMLImageElement} img
   * @param {string} src
   * @param {boolean} productShot
   */
  #setSwatchImage(img, src, productShot) {
    const width = productShot ? SWATCH_SIZE * PRODUCT_SHOT_SCALE : SWATCH_SIZE;

    img.className = productShot
      ? 'product-swatches__swatch-image product-swatches__swatch-image--product-shot'
      : 'product-swatches__swatch-image';
    img.src = sizedImageUrl(src, width);
    img.srcset = SWATCH_IMAGE_DPRS.map((dpr) => `${sizedImageUrl(src, width * dpr)} ${dpr}x`).join(', ');
  }

  /**
   * Show the hovered print's name and prefetch its section, putting the selected name back on the way
   * out. component.js delegates a fixed event list and resolves these two by exact `hasAttribute`, so
   * `on:pointerenter` sits on each swatch and `on:pointerleave` on the selector.
   *
   * @param {Event} event
   */
  previewPrint(event) {
    const target = event.target;
    const entering = event.type === 'pointerenter' && target instanceof HTMLAnchorElement;
    const result = entering ? this.#resultsByProductId.get(target.dataset.productId ?? '') : null;

    this.#writePrintName(getProductTitle(result) || this.#activePrintName);

    // warm the section this print would render, so the click has it in hand. hover only: a tap fires
    // pointerenter too, and would race its own click.
    if (entering && matchMedia('(hover: hover)').matches) {
      this.#fetchSection(this.#getVariantUrlBySize(target, result))?.catch(() => {});
    }
  }

  /**
   * Mark the clicked swatch, emit the pick, and swap the product unless a listener cancels or the
   * click was modified.
   *
   * @param {Event} event
   */
  /** @type {Map<string, Promise<any>>} A product by handle, so no print is fetched twice. */
  #productsByHandle = new Map();

  /**
   * Fetch the products a print is made of, as the Ajax Product API describes them.
   *
   * Fetched here rather than by whoever listens: this component is the one that knows a print
   * can be more than one garment, so it hands over what it found rather than handles to chase.
   *
   * @param {any} result
   * @returns {Promise<any[]>}
   */
  async #fetchProducts(result) {
    const matches = Array.isArray(result?.matches) ? result.matches : [];
    const root = window.Shopify?.routes?.root ?? '/';

    return Promise.all(
      matches.map((match) => {
        const handle = String(match?.url ?? '').split('/').pop()?.split('?')[0] ?? '';
        if (!handle) return null;

        const held = this.#productsByHandle.get(handle);
        if (held) return held;

        const request = fetch(`${root}products/${handle}.js`)
          .then((response) => {
            if (!response.ok) throw new Error(`Product ${handle}: ${response.status}`);

            return response.json();
          })
          .catch((error) => {
            console.error(error);
            this.#productsByHandle.delete(handle);

            return null;
          });

        this.#productsByHandle.set(handle, request);

        return request;
      })
    );
  }

  selectSwatch(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest(SWATCH_SELECTOR);
    if (!(link instanceof HTMLAnchorElement)) return;

    // let the browser open new tabs and windows
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    if (link.hasAttribute('aria-current')) return;

    const links = /** @type {HTMLAnchorElement[]} */ ([...this.refs.grid.querySelectorAll(SWATCH_SELECTOR)]);
    this.dataset.productId = link.dataset.productId ?? '';
    this.#markSelectedSwatch(links, this.dataset.productId);

    const result = this.#resultsByProductId.get(this.dataset.productId);

    // Several garments in one print have no single page to re-render, and #emit answers false
    // for none of them — so only a print that is one product sends for a page.
    const emitted = this.#emit(SsPrintSwatches.swatchUpdate, result);

    if (emitted && !(result?.matches?.length > 1)) {
      this.#rerenderSection(this.#getVariantUrlBySize(link, result));
    }
  }

  /**
   * Get the new variant url of a swatch, carrying over the selected size. Read from the result's own
   * `variants`, so it costs no request.
   *
   * @param {HTMLAnchorElement} link
   * @param {any} result
   * @returns {string}
   */
  #getVariantUrlBySize(link, result) {
    const url = new URL(link.href);
    url.searchParams.delete('variant');

    const variant = findVariantBySize(result, this.dataset.size);
    if (variant?.variant_id) url.searchParams.set('variant', String(variant.variant_id));

    return `${url.pathname}${url.search}`;
  }

  /**
   * Fetch this selector's section rendered at a print's url. Cached and deduped by `sectionRenderer`, so a
   * hover and the click that follows it share one request.
   *
   * @param {string} path
   * @returns {Promise<string> | null} Null when the host named no section to render.
   */
  #fetchSection(path) {
    const sectionId = this.dataset.morphSectionId;
    if (!sectionId) return null;

    return sectionRenderer.getSectionHTML(sectionId, true, new URL(path, window.location.origin));
  }

  /**
   * Swap the page's product for the picked print, by morphing in `data-morph-section-id` rendered at
   * its url. The size and purchase option are carried over, and the url is pushed if this selector owns it.
   *
   * Morphed here rather than via `morphSection`, which passes no `getNodeKey`: without it the variant
   * picker's pill and label spans mispair and a button loses its label. The picker is poked afterwards
   * because a DOMParser document never upgrades custom elements.
   *
   * @param {string} path
   * @param {boolean} [pushHistory] - False when the swap IS a history move.
   */
  async #rerenderSection(path, pushHistory = true) {
    const sectionId = this.dataset.morphSectionId;
    if (this.closest('quick-add-dialog')) {
      document.dispatchEvent(new CustomEvent('quickAdd:loadProduct', { detail: { path } }));
      return;
    }

    const section = sectionId && document.getElementById(buildSectionSelector(sectionId));
    if (!section) return;

    // read before the morph re-adds the attribute
    const ownsUrl = this.#ownsUrl;

    try {
      const html = await this.#fetchSection(path);
      const parsed = new DOMParser().parseFromString(String(html), 'text/html');
      const incoming = parsed.getElementById(buildSectionSelector(sectionId));
      if (!incoming) throw new Error(`Section ${sectionId} missing from the response`);

      // restore user state across the morph
      const purchaseOption = getCheckedPurchaseOption(section);
      const { size } = this.dataset;
      const openGroup = this.openGroup;

      // This element opts out of attribute morphing, so the print the response is about has to be
      // handed over by name — a history move renders a product the shopper never clicked, and the
      // ring would otherwise stay on the one they did.
      const renderedProductId = incoming.querySelector('ss-print-swatches')?.getAttribute('data-product-id');

      const checkedOptionValueIds = [
        ...incoming.querySelectorAll('fieldset input[data-option-value-id][checked]'),
      ].map((input) => input.getAttribute('data-option-value-id'));

      morph(section, incoming, { ...MORPH_OPTIONS, getNodeKey: nodeKey });

      // Before the pickers below re-read their state off the DOM.
      for (const optionValueId of checkedOptionValueIds) {
        const input = section.querySelector(`fieldset input[data-option-value-id="${optionValueId}"]`);
        if (input instanceof HTMLInputElement && !input.checked) input.checked = true;
      }

      if (renderedProductId) this.dataset.productId = renderedProductId;
      if (size) this.dataset.size = size;
      if (purchaseOption) restorePurchaseOption(section, purchaseOption);

      for (const element of section.querySelectorAll('variant-picker, size-guide-tabs')) {
        if (element instanceof Component && element.isConnected) element.updatedCallback();
      }

      // The gallery element survives the morph holding its own scroll position and `current` index,
      // while the incoming markup marks the first thumbnail selected — reset it so the two agree.
      // It also survives wired for the slides it had, and a Mystery print carries a single picture,
      // so its slideshow was set up with no scroller and the arrows of the print morphed over it do
      // nothing. `morph` hands its post-update hook the parsed response node, so `updatedCallback`
      // never reaches the element actually morphed; it is told here instead.
      const gallery = section.querySelector('media-gallery');
      if (gallery instanceof MediaGallery) {
        gallery.updatedCallback();
        gallery.slideshow?.updatedCallback();
        gallery.slideshow?.select(0, undefined, { animate: false });
      }

      // The tabs morph, because another product type offers another set of groups — so the
      // shopper's own group is re-opened over the one the response marked. The pill's inline
      // position went with them, since the server renders none.
      this.presetGroup(openGroup);
      this.#moveTabIndicator();

      if (pushHistory && ownsUrl) history.pushState({}, '', path);
    } catch (error) {
      console.error(error);
      // only a selector that owns the URL may navigate
      if (ownsUrl) window.location.assign(path);
    }
  }

  /** @param {Event} event */
  #prefetchFromTouch = (event) => {
    const target = event.target;
    const link = target instanceof Element ? target.closest(SWATCH_SELECTOR) : null;
    if (!(link instanceof HTMLAnchorElement)) return;

    const result = this.#resultsByProductId.get(link.dataset.productId ?? '');
    this.#fetchSection(this.#getVariantUrlBySize(link, result))?.catch(() => {});
  };

  #restoreFromHistory = () => {
    if (!this.#ownsUrl) return;

    this.#rerenderSection(`${window.location.pathname}${window.location.search}`, false);
  };

  /** Whether the page is about this selector's product, rather than the selector being one of several on it. */
  get #ownsUrl() {
    return this.hasAttribute('data-update-url');
  }
}

if (!customElements.get('ss-print-swatches')) {
  customElements.define('ss-print-swatches', SsPrintSwatches);
}
